import { useEffect, useRef, useState } from 'react';
import { GRAND_ALL, fmtDay, fmtDayLong } from '../campaign';
import { BgRot, Crop, GoldButton, PrizeArt, Sheet, makeSparks } from './Shared';
import { BackIcon, CloseIcon, RewardIcon } from './Icons';
import { doorFor, fillCount, isAnswered, qWord, surveyMaxLength, surveyMinLength, surveyPath, surveyProgress } from '../survey';
import artSurvey from '../assets/art_survey.webp';
import chestc from '../assets/chestc.webp';
import gemImg from '../assets/gem.webp';

/* ------------------------------------------------------------------
   The quick survey (prototype #scr-survey): a full-screen page with one
   question at a time — the opening question picks the "door", the set of
   follow-ups — progress bars, the reward chip, and Next / "Finish & claim
   your Chest". Finishing plays the claim moment: a "Survey complete" tick,
   then the reward stage slides up, a pillar rises as the Golden Chest
   swoops in and lands on it. Before the reveal day the chest sits locked
   ("Opens on 13 Nov"); from the reveal day on it can be opened right there
   — hold the button or tap the chest — and the prize bursts out of it.
   Also here: SurveySheet (the bottom sheet that introduces / resumes it)
   and SurveyRow (its entry on the rewards page).

   The same page plays the Quote & Claim chest's claim moment (`mode:
   'quote'`, prototype svClaimAnim({ skipIntro })): no questions and no
   "Survey complete" beat — the claim call runs while the page breathes
   for a moment, then the stage rises and the chest lands, eyebrow
   "Quote & Claim reward". A declined claim closes the page.

   Props: `survey` is the mapped API survey (copy, q1, doors; unused in
   quote mode); `initial` the saved { answers, door } to resume from;
   `drawOn` the reveal day (grandPrizes.drawOn) and `locked` whether it
   is still ahead; `onExit(answers, door)` closes (the parent saves
   progress); `onAnswer(questionId, answerText)` reports each question as
   it is submitted (Next, or Finish on the last one) — the parent tracks
   it; `onSubmit(answers, door)` is the API call, resolving to the
   credited chest; `onError(err)` reports a declined submit; `onHome(won)`
   is "Back to the Quest" (`won` names the prize when the chest was opened
   here); `onToast(msg)` shows a toast; `eyebrow` the reward stage's
   label ("Survey reward").
   ------------------------------------------------------------------ */

const OUT_MS = 200;      // the question slides out before the next slides in
const HOLD_MS = 2000;    // press-and-hold to open the chest
/* the stage geometry the opening plays around (375×812 design px) */
const CX = 187.5;        // centre
const CY0 = 302;         // the chest's centre on its pillar
const CY = 400;          // where it floats to, and the prize appears
const PRIZE_RISE = -138; // the prize lifts to leave room for the copy
const vib = (p) => { try { if (navigator.vibrate) navigator.vibrate(p); } catch (e) { /* unsupported */ } };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
/* cancel the WAAPI animations under `el` (CSS-driven ones stay) */
const cancelApi = (el) => {
  if (!el || !el.getAnimations) return;
  el.getAnimations({ subtree: true }).forEach((a) => { if (!(window.CSSAnimation && a instanceof window.CSSAnimation)) a.cancel(); });
};

const Check = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3L9.5 3.8" stroke="#2A1450" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* "Finish to claim a Golden Chest" with the chest in bold, like the prototype */
function BoldChest({ text }) {
  const at = text.indexOf('Golden Chest');
  if (at < 0) return text;
  return <>{text.slice(0, at)}<b>Golden Chest</b>{text.slice(at + 'Golden Chest'.length)}</>;
}

/* the reward chip's line for the current standing */
function chipText(copy, pr) {
  if (pr.done > 0) return pr.rem > 0 ? fillCount(copy.chipRemain, pr.rem) : copy.chipReady;
  return copy.chip;
}

/* the submitted answer as text: the option chosen, or the options chosen
   joined with " | " (an option's own text can hold a comma) */
function answerText(Q, a) {
  const opt = (i) => (Q.opts[i] !== undefined ? Q.opts[i] : String(i));
  return Array.isArray(a) ? a.map(opt).join(' | ') : opt(a);
}

/* keep only the opening answer and the chosen door's answers */
function pruneAnswers(survey, answers, door) {
  const keep = new Set(surveyPath(survey, door).map((q) => q.id));
  const out = {};
  Object.keys(answers).forEach((k) => { if (keep.has(k)) out[k] = answers[k]; });
  return out;
}

/* what an opened chest shows: the backend's `result` on the chest it
   credited — a grand prize (pictured when we have its render), Gold or
   Sats — or, when the result isn't in yet, the chest itself and a
   "prize on its way" line. `won` is what the hub's toast names. */
function prizeFor(chest) {
  const r = chest && chest.result;
  const chestArt = <Crop kind="prod" src={chestc} />;
  if (!r) return { won: null, title: 'Your Golden Chest is open!', sub: 'Your prize is on its way — we’ll let you know.', art: chestArt };
  const name = r.prize || (r.kind === 'GOLD' ? 'Gold' : r.kind === 'SATS' ? 'Sats' : 'a prize');
  const an = /^[aeiou]/i.test(name) ? 'an' : 'a';
  if (r.kind === 'GRAND') {
    const known = GRAND_ALL.some((g) => g.name === name);
    return { won: name, title: `You won ${an} ${name}!`, sub: 'We’ll call you to arrange delivery.', art: known ? <PrizeArt prize={{ name }} /> : chestArt };
  }
  return { won: name, title: `You won ${name}!`, sub: 'Credited to your GoSats wallet.', art: <div className="gq-xric"><RewardIcon kind={r.kind} /></div> };
}

/* quote mode has no questions: a one-question stand-in keeps the page's
   path / progress maths (never shown) well-defined */
const NO_SURVEY = { copy: {}, q1: { id: 'q1', q: '', hint: '', type: 'one', max: 99, grid: false, opts: ['—'] }, doors: {} };

export default function Survey({
  survey: surveyProp, initial, drawOn, locked = true, zoom = 1, stageHeight = 812, mode = 'survey', eyebrow: eyeLabel = 'Survey reward',
  onExit, onAnswer, onSubmit, onError, onHome, onToast,
}) {
  const quote = mode === 'quote';
  const survey = surveyProp || NO_SURVEY;
  const { copy } = survey;
  const [answers, setAnswers] = useState(() => (initial && initial.answers) || {});
  const [door, setDoor] = useState(() => (initial && initial.door) || doorFor(survey, (initial && initial.answers) || {}));
  /* the question on screen; `dir` drives the slide (null on the first render), `seq` remounts it */
  const [view, setView] = useState(() => {
    const d = (initial && initial.door) || doorFor(survey, (initial && initial.answers) || {});
    const pr = surveyProgress(survey, { answers: (initial && initial.answers) || {}, door: d });
    return { i: Math.min(pr.done, surveyPath(survey, d).length - 1), dir: null, seq: 0 };
  });
  const [leaving, setLeaving] = useState(false);
  const [phase, setPhase] = useState('q');     // q | claiming
  const [stage, setStage] = useState(0);       // the reward stage: 0 hidden · 1 shown · 2 text in · 3 ready
  /* opening the chest (reveal day on): the hold, the burst, the prize */
  const [holding, setHolding] = useState(false);
  const [opened, setOpened] = useState(false);     // the opening has started
  const [revealed, setRevealed] = useState(false); // the prize and its copy are up
  const [chest, setChest] = useState(null);        // the chest the backend credited
  const refs = {
    q: useRef(null), hint: useRef(null), prog: useRef(null), comp: useRef(null),
    done: useRef(null), stage: useRef(null), fog: useRef(null), pillar: useRef(null), cshadow: useRef(null),
    chest: useRef(null), shake: useRef(null), fx: useRef(null), dust: useRef(null), lock: useRef(null),
    back: useRef(null), front: useRef(null), prize: useRef(null), hf: useRef(null),
  };
  const tok = useRef(0);
  const alive = useRef(true);
  const timers = useRef([]);
  const hold = useRef({ raf: 0, t0: 0, lastVib: 0 });
  const opening = useRef(false);
  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    const h = hold.current;
    return () => {
      alive.current = false;
      tok.current += 1;
      pending.forEach(clearTimeout);
      cancelAnimationFrame(h.raf);
    };
  }, []);
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };

  const path = surveyPath(survey, door);
  const Q = path[Math.min(view.i, path.length - 1)];
  const a = answers[Q.id];
  const many = Q.type === 'many';
  const limited = many && Q.max < 99;
  const count = many ? (a || []).length : 0;
  const full = limited && count >= Q.max;
  const answered = isAnswered(a);
  /* Q1 alone is never the end: the door picks the rest of the path */
  const branching = Object.keys(survey.doors || {}).length > 0;
  const last = (!branching || !!door) && view.i >= path.length - 1;
  const total = door ? path.length : surveyMaxLength(survey);
  const pr = surveyProgress(survey, { answers, door });
  const selected = (v) => (many ? (a || []).includes(v) : a === v);

  /* the progress bars fade when the path length settles (the door is picked) */
  const lastTotal = useRef(total);
  useEffect(() => {
    if (lastTotal.current !== total && refs.prog.current) {
      refs.prog.current.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 300 });
    }
    lastTotal.current = total;
  }, [total, refs.prog]);

  /* ---- answering ---- */
  const pick = (v) => {
    if (phase !== 'q' || leaving) return;
    let next;
    if (!many) {
      next = { ...answers, [Q.id]: v };
    } else {
      const list = (a || []).slice();
      const at = list.indexOf(v);
      if (at >= 0) list.splice(at, 1);
      else {
        if (list.length >= Q.max) {
          vib(8);
          if (refs.hint.current) refs.hint.current.animate([{ transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'none' }], { duration: 240 });
          return;
        }
        list.push(v);
      }
      next = { ...answers, [Q.id]: list };
    }
    if (view.i === 0) {
      const d = doorFor(survey, next);
      if (d !== door) { setDoor(d); next = pruneAnswers(survey, next, d); }
    }
    setAnswers(next);
  };

  const show = (i, dir) => setView((v) => ({ i, dir, seq: v.seq + 1 }));
  const goNext = () => {
    if (!answered || phase !== 'q' || leaving) return;
    /* the answer is in — Next, or Finish on the last question */
    if (onAnswer) onAnswer(Q.id, answerText(Q, a));
    if (last) { finish(); return; }
    setLeaving(true);
    later(() => { setLeaving(false); show(view.i + 1, 'fwd'); }, OUT_MS);
  };
  const exit = () => { if (phase === 'q') onExit(answers, door); };
  const goBack = () => {
    if (phase !== 'q' || leaving) return;
    if (view.i === 0) { exit(); return; }
    show(view.i - 1, 'back');
  };

  /* ---- the claim moment (prototype svClaimAnim) ----
     The "Survey complete" beat plays while the submit is in flight; the
     stage follows once both are through. A declined submit returns to the
     last question and reports the backend's reason. */
  const finish = () => {
    if (phase !== 'q') return;
    setPhase('claiming');
    claim({ answers, door });
  };
  const claim = async (payload, skipIntro = false) => {
    const t = ++tok.current;
    const ok = () => t === tok.current && alive.current;
    const A = (el, k, o) => (el ? el.animate(k, { fill: 'both', easing: 'cubic-bezier(.2,.8,.2,1)', ...o }).finished.catch(() => {}) : Promise.resolve());
    const submit = onSubmit(payload.answers, payload.door).then((chest) => ({ chest }), (err) => ({ err }));
    await nextFrame(); if (!ok()) return;
    const comp = refs.comp.current;
    if (skipIntro) {
      /* Quote & Claim: no survey beat — a breath while the claim is in flight */
      await wait(380); if (!ok()) return;
      const res = await submit; if (!ok()) return;
      if (res.err) { onError(res.err); onExit({}, null); return; }
      setChest(res.chest || null);
    } else {
      /* 1 · survey complete: the bars fill, a tick draws itself */
      A(comp, [{ transform: 'translateY(18px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 450, delay: 120 });
      A(comp && comp.querySelector('.gq-ok'), [{ transform: 'scale(.4)' }, { transform: 'scale(1.12)', offset: 0.6 }, { transform: 'scale(1)' }], { duration: 520, delay: 120, easing: 'cubic-bezier(.2,1.3,.3,1)' });
      A(comp && comp.querySelector('.gq-tk'), [{ strokeDashoffset: 60 }, { strokeDashoffset: 0 }], { duration: 420, delay: 420, easing: 'cubic-bezier(.6,0,.3,1)' });
      later(() => { if (ok()) vib(14); }, 480);
      await wait(1600); if (!ok()) return;
      const res = await submit; if (!ok()) return;
      if (res.err) {
        /* back to the last question; the parent shows why */
        await A(comp, [{ opacity: 1 }, { opacity: 0 }], { duration: 220, easing: 'ease', fill: 'forwards' });
        if (!ok()) return;
        cancelApi(comp);
        setPhase('q');
        show(view.i, null);
        onError(res.err);
        return;
      }
      A(comp, [{ transform: 'none', opacity: 1 }, { transform: 'translateY(-24px) scale(.96)', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
      setChest(res.chest || null);
      await wait(260); if (!ok()) return;
    }
    /* 2 · the reward stage slides up over the survey */
    setStage(1); vib(8);
    const rise = { duration: 650, easing: 'cubic-bezier(.22,1,.36,1)' };
    A(refs.stage.current, [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], rise);
    A(refs.fog.current, [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], rise);
    await wait(560); if (!ok()) return;
    /* 3 · the pillar rises from below as the chest swoops in from above — they meet */
    A(refs.pillar.current, [{ transform: 'translateY(560px)', opacity: 1 }, { transform: 'translateY(0)', opacity: 1 }], { duration: 720, easing: 'cubic-bezier(.3,.75,.35,1)' });
    A(refs.cshadow.current, [{ opacity: 0 }, { opacity: 0, offset: 0.78 }, { opacity: 1 }], { duration: 720, easing: 'linear' });
    await A(refs.chest.current, [{ transform: 'translateY(-540px)', opacity: 1 }, { transform: 'translateY(0)', opacity: 1 }], { duration: 720, easing: 'cubic-bezier(.45,0,.85,.45)' });
    if (!ok()) return;
    /* 4 · the landing: light floods in, the chest squashes, the pillar gives, dust kicks up */
    vib([0, 45, 20, 55]);
    A(refs.fx.current, [{ opacity: 0 }, { opacity: 1 }], { duration: 900, easing: 'ease-out' });
    A(refs.shake.current, [{ transform: 'scale(1,1)' }, { transform: 'scale(1.1,.88) translateY(8px)', offset: 0.28 }, { transform: 'scale(.97,1.04) translateY(-5px)', offset: 0.62 }, { transform: 'scale(1,1)' }], { duration: 460, easing: 'ease-out', fill: 'none' });
    if (refs.pillar.current) refs.pillar.current.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(5px)', offset: 0.3 }, { transform: 'translateY(0)' }], { duration: 420, easing: 'ease-out', composite: 'add' });
    const dust = refs.dust.current;
    if (dust) {
      dust.innerHTML = '';
      for (let i = 0; i < 12; i += 1) {
        const p = document.createElement('i');
        dust.appendChild(p);
        const side = i % 2 ? 1 : -1;
        const dx = side * (30 + Math.random() * 80);
        const dy = -(2 + Math.random() * 16);
        const x0 = 187.5 + side * (30 + Math.random() * 50);
        p.animate(
          [{ transform: `translate(${x0.toFixed(0)}px,384px) scale(.45)`, opacity: 0 }, { opacity: 0.7, offset: 0.15 }, { transform: `translate(${(x0 + dx).toFixed(0)}px,${(384 + dy).toFixed(0)}px) scale(.9)`, opacity: 0 }],
          { duration: 620 + Math.random() * 260, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'both' },
        );
      }
    }
    await wait(460); if (!ok()) return;
    setStage(2);
    await wait(420); if (ok()) setStage(3);
  };

  /* quote mode: the claim moment starts as the page opens (prototype: the
     card's "Claim Golden Chest" → the survey screen in xmode, skipIntro) */
  const claimRef = useRef(claim);
  claimRef.current = claim;
  useEffect(() => {
    if (!quote) return;
    setPhase('claiming');
    claimRef.current({ answers: {}, door: null }, true);
  }, [quote]);

  /* ---- opening the chest (prototype svOpenChest) ----
     From the reveal day on. The chest squashes and lifts off its pillar as
     the pillar sinks away, a ripple and rays build, then a flash — the chest
     is gone and Gems, confetti and light lines fountain out while the prize
     lands dead centre; it then rises to make room for the copy. */
  const openChest = async () => {
    if (locked || stage < 3 || opening.current) return;
    const d = refs.done.current; const back = refs.back.current; const front = refs.front.current;
    const cw = refs.chest.current; const sh = refs.shake.current; const pz = refs.prize.current;
    if (!d || !back || !front || !cw || !sh || !pz) return;
    opening.current = true;
    const t = tok.current;
    const ok = () => t === tok.current && alive.current;
    setOpened(true);
    stopHold(false);
    sh.getAnimations().forEach((a) => a.cancel());
    const R = Math.random;
    const A = (e, k, o) => (e ? e.animate(k, { fill: 'both', easing: 'cubic-bezier(.2,.8,.2,1)', ...o }) : null);
    const mk = (p, cls, css, html) => {
      const e = document.createElement('div');
      e.className = cls;
      if (html) e.innerHTML = html;
      Object.assign(e.style, css || {});
      p.appendChild(e);
      return e;
    };
    const at = (ms, f) => later(() => { if (ok()) f(); }, ms);
    back.innerHTML = ''; front.innerHTML = '';

    /* 0 · anticipation: the light builds behind, the chest braces */
    vib(20);
    const bg = mk(back, 'gq-xbg', null, '<div class="gq-xglow"></div><div class="gq-xvig"></div>');
    bg.querySelector('.gq-xglow').style.top = `${CY}px`;
    A(bg, [{ opacity: 0 }, { opacity: 1 }], { duration: 450, easing: 'ease-out' });
    A(bg.querySelector('.gq-xglow'), [{ transform: 'scale(.5)', opacity: 0.4 }, { transform: 'scale(1)', opacity: 1, offset: 0.4 }, { transform: 'scale(1.25)', opacity: 1, offset: 0.42 }, { transform: 'scale(1)', opacity: 0.85 }], { duration: 3000, easing: 'ease-out' });
    A(sh, [
      { transform: 'scale(1,1)', filter: 'drop-shadow(0 0 14px rgba(253,189,24,.6)) brightness(1)' },
      { transform: 'scale(1.08,.88) translateY(8px)', offset: 0.35 },
      { transform: 'scale(.96,1.06) translateY(-4px)', offset: 0.7 },
      { transform: 'scale(1,1)', filter: 'drop-shadow(0 0 22px rgba(253,189,24,.9)) brightness(1.1)' },
    ], { duration: 380, easing: 'cubic-bezier(.3,0,.3,1)' });

    /* the pillar and its shadow sink away — the chest floats to centre stage */
    A(refs.cshadow.current, [{ opacity: 1 }, { opacity: 0 }], { duration: 160, delay: 200, fill: 'forwards' });
    A(refs.pillar.current, [{ transform: 'translateY(0)' }, { transform: 'translateY(580px)' }], { duration: 540, delay: 200, easing: 'cubic-bezier(.55,0,.8,.4)', fill: 'forwards' });
    A(refs.fog.current, [{ transform: 'translateY(0)' }, { transform: 'translateY(600px)' }], { duration: 560, delay: 300, easing: 'cubic-bezier(.55,0,.8,.4)', fill: 'forwards' });
    A(refs.fx.current, [{ opacity: 1 }, { opacity: 0 }], { duration: 600, delay: 300, fill: 'forwards' });
    const lift = [{ transform: 'translateY(0) scale(1) rotate(0)' }];
    for (let k = 1; k <= 12; k += 1) {
      const f = k / 12; const amp = f * f * 5; const ty = (CY - CY0) * Math.min(1, f * 1.5);
      lift.push({ offset: f, transform: `translateY(${ty.toFixed(1)}px) scale(${(1 + 0.3 * f).toFixed(3)}) rotate(${(k % 2 ? amp : -amp).toFixed(2)}deg)` });
    }
    A(cw, lift, { duration: 900, delay: 380, easing: 'cubic-bezier(.4,0,.6,1)' });
    A(sh, [{ filter: 'drop-shadow(0 0 22px rgba(253,189,24,.9)) brightness(1.1)' }, { filter: 'drop-shadow(0 0 46px rgba(255,214,120,1)) brightness(1.9)' }], { duration: 900, delay: 380, easing: 'ease-in', fill: 'forwards' });
    [420, 640, 860, 1060].forEach((ms, i) => at(ms, () => vib(6 + i * 4)));

    /* ripple: arcs spin out from the chest */
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'gq-xarc'); svg.setAttribute('viewBox', '0 0 375 812'); back.appendChild(svg);
    for (let i = 0; i < 6; i += 1) {
      const g = document.createElementNS(NS, 'g'); g.style.transformOrigin = `${CX}px ${CY}px`;
      const r = 64; const C = 2 * Math.PI * r;
      [0, 1].forEach((k) => {
        const c = document.createElementNS(NS, 'circle');
        c.setAttribute('cx', CX); c.setAttribute('cy', CY); c.setAttribute('r', r); c.setAttribute('fill', 'none');
        c.setAttribute('stroke', k ? 'rgba(255,255,255,.85)' : 'rgba(214,150,30,.75)');
        c.setAttribute('stroke-width', String(i % 2 ? 3 : 5)); c.setAttribute('stroke-linecap', 'round');
        c.setAttribute('stroke-dasharray', `${(C * 0.36).toFixed(1)} ${(C * 0.64).toFixed(1)}`);
        c.setAttribute('stroke-dashoffset', (-C * (k * 0.5 + i * 0.13)).toFixed(1));
        g.appendChild(c);
      });
      svg.appendChild(g);
      const dir = i % 2 ? 1 : -1;
      A(g, [{ transform: 'rotate(0deg) scale(.6)', opacity: 0 }, { opacity: 1, offset: 0.18 }, { transform: `rotate(${dir * 150}deg) scale(${(4.2 + i * 0.25).toFixed(2)})`, opacity: 0 }], { duration: 1500, delay: 600 + i * 150, easing: 'cubic-bezier(.15,.55,.25,1)' });
    }

    /* rays, turning slowly behind everything that follows */
    const rays = mk(back, 'gq-xrays2', { left: `${CX - 450}px`, top: `${CY - 450}px` });
    A(rays, [{ transform: 'rotate(-10deg) scale(.4)', opacity: 0 }, { transform: 'rotate(8deg) scale(1)', opacity: 1, offset: 0.5 }, { transform: 'rotate(18deg) scale(1.05)', opacity: 0.55 }], { duration: 1700, delay: 800, easing: 'cubic-bezier(.2,.7,.2,1)' });
    rays.animate([{ rotate: '0deg' }, { rotate: '360deg' }], { duration: 60000, iterations: Infinity, delay: 2500 });
    A(rays, [{ translate: '0 0' }, { translate: `0 ${PRIZE_RISE}px` }], { duration: 750, delay: 2250, fill: 'forwards', composite: 'add' });

    /* the burst: a flash swallows the chest, a shockwave and light lines fly */
    const fl = mk(front, 'gq-xflash', { left: `${CX - 75}px`, top: `${CY - 75}px` });
    A(fl, [{ transform: 'scale(.2)', opacity: 0 }, { transform: 'scale(1.5)', opacity: 1, offset: 0.35 }, { transform: 'scale(2.2)', opacity: 0 }], { duration: 700, delay: 1260, easing: 'cubic-bezier(.2,.8,.3,1)' });
    A(cw, [{ opacity: 1 }, { opacity: 0 }], { duration: 160, delay: 1330, fill: 'forwards' });
    const sw = mk(front, 'gq-xshock', { left: `${CX - 60}px`, top: `${CY - 60}px`, width: '120px', height: '120px' });
    A(sw, [{ transform: 'scale(.4)', opacity: 0, borderWidth: '14px' }, { transform: 'scale(.6)', opacity: 1, offset: 0.08 }, { transform: 'scale(4.2)', opacity: 0, borderWidth: '1px' }], { duration: 950, delay: 1300, easing: 'cubic-bezier(.1,.6,.3,1)' });
    for (let i = 0; i < 16; i += 1) {
      const a = (i / 16) * 360 + R() * 10; const len = 70 + R() * 60;
      const l = mk(front, 'gq-xline', { left: `${CX - 1}px`, top: `${CY}px`, height: `${len}px` });
      A(l, [{ transform: `rotate(${a}deg) translateY(50px)`, opacity: 0 }, { opacity: 1, offset: 0.25 }, { transform: `rotate(${a}deg) translateY(${260 + R() * 140}px)`, opacity: 0 }], { duration: 520, delay: 1300 + R() * 80, easing: 'cubic-bezier(.2,.7,.4,1)' });
    }
    at(1290, () => vib([0, 60, 30, 90]));

    /* a fountain of Gems… */
    for (let i = 0; i < 10; i += 1) {
      const sz = 22;
      const e = mk(front, 'gq-xfly gq-gemf', { left: `${CX - sz / 2}px`, top: `${CY - 20}px`, width: `${sz}px`, height: `${sz * 1.21}px` }, `<img src="${gemImg}" alt="">`);
      const vx = (R() * 2 - 1) * 230; const vy = -(420 + R() * 260); const g = 980; const T = 1.6 + R() * 0.4; const spin = (R() * 2 - 1) * 600; const sc = 0.9 + R() * 0.7;
      const fr = [];
      for (let k = 0; k <= 14; k += 1) {
        const tt = (k / 14) * T; const x = vx * tt; const y = vy * tt + 0.5 * g * tt * tt;
        fr.push({ offset: k / 14, transform: `translate(${x.toFixed(1)}px,${y.toFixed(1)}px) rotate(${((spin * k) / 14).toFixed(0)}deg) scale(${(k === 0 ? 0.3 : sc).toFixed(2)})`, opacity: k === 0 ? 0 : (k > 11 ? (14 - k) / 3 : 1) });
      }
      A(e, fr, { duration: T * 1000, delay: 1320 + R() * 260, easing: 'linear' });
    }
    /* …and confetti, purple and gold, fluttering down */
    const CC = ['#8A4BE0', '#B88AF5', '#5B2DA3', '#F4C04A', '#FFE08A', '#E9A93A'];
    for (let i = 0; i < 36; i += 1) {
      const e = mk(front, 'gq-xfly gq-conf', { left: `${CX - 3}px`, top: `${CY - 20}px`, background: CC[i % CC.length] });
      const vx = (R() * 2 - 1) * 260; const vy = -(380 + R() * 320); const g = 900; const T = 1.8 + R() * 0.6; const rz = (R() * 2 - 1) * 720; const rx = 360 + R() * 720;
      const fr = [];
      for (let k = 0; k <= 16; k += 1) {
        const tt = (k / 16) * T; const drag = 1 - Math.min(0.55, tt * 0.28); const x = vx * tt * drag + Math.sin(tt * 9 + i) * 10; const y = vy * tt + 0.5 * g * tt * tt;
        fr.push({ offset: k / 16, transform: `translate(${x.toFixed(1)}px,${y.toFixed(1)}px) rotate(${((rz * k) / 16).toFixed(0)}deg) rotateX(${((rx * k) / 16).toFixed(0)}deg) scale(${k === 0 ? 0.3 : 1})`, opacity: k === 0 ? 0 : (k > 13 ? (16 - k) / 3 : 1) });
      }
      A(e, fr, { duration: T * 1000, delay: 1300 + R() * 220, easing: 'linear' });
    }

    /* the prize, dead centre */
    const halo = pz.querySelector('.gq-halo'); const prod = pz.querySelector('.gq-prod'); const floor = pz.querySelector('.gq-floor');
    A(pz, [{ opacity: 0 }, { opacity: 1 }], { duration: 200, delay: 1400 });
    A(halo, [{ transform: 'scale(.3)', opacity: 0 }, { transform: 'scale(1.15)', opacity: 0.45, offset: 0.5 }, { transform: 'scale(1)', opacity: 0.3 }], { duration: 900, delay: 1400 });
    A(prod, [
      { transform: 'scale(.2) rotate(-35deg)', opacity: 0, filter: 'drop-shadow(0 18px 22px rgba(0,0,0,.32)) brightness(2.6)' },
      { transform: 'scale(1.12) rotate(7deg)', opacity: 1, filter: 'drop-shadow(0 18px 22px rgba(0,0,0,.32)) brightness(1.3)', offset: 0.55 },
      { transform: 'scale(.97) rotate(-2deg)', offset: 0.8 },
      { transform: 'scale(1) rotate(0)', opacity: 1, filter: 'drop-shadow(0 18px 22px rgba(0,0,0,.32)) brightness(1)' },
    ], { duration: 950, delay: 1430, easing: 'cubic-bezier(.2,1,.3,1)' });
    A(floor, [{ transform: 'scale(.3)', opacity: 0 }, { transform: 'scale(1)', opacity: 0.7 }], { duration: 700, delay: 1600 });
    pz.querySelectorAll('.gq-tw').forEach((tw, i) => tw.animate([{ transform: 'scale(0) rotate(0)', opacity: 0 }, { transform: 'scale(1) rotate(45deg)', opacity: 1, offset: 0.5 }, { transform: 'scale(0) rotate(90deg)', opacity: 0 }], { duration: 1400, delay: 1900 + i * 260, iterations: Infinity, easing: 'ease-in-out' }));
    at(1450, () => {
      makeSparks(CX, CY, 14).forEach((s) => {
        const i = document.createElement('i'); i.className = 'gq-spark';
        Object.entries(s.style).forEach(([k, v]) => { if (k.startsWith('--')) i.style.setProperty(k, v); else i.style[k] = typeof v === 'number' ? `${v}px` : v; });
        front.appendChild(i);
        later(() => i.remove(), 1300);
      });
      vib(26);
    });
    at(1850, () => vib(12));
    [1760, 2100].forEach((ms) => {
      const p = mk(front, 'gq-xpulse', { left: `${CX - 125}px`, top: `${CY - 125}px`, width: '250px', height: '250px' });
      A(p, [{ transform: 'scale(.92)', opacity: 0 }, { transform: 'scale(.98)', opacity: 0.9, offset: 0.06 }, { transform: 'scale(1.6)', opacity: 0 }], { duration: 1100, delay: ms, easing: 'cubic-bezier(.2,.7,.3,1)' });
    });
    /* glitter drifting down */
    for (let i = 0; i < 8; i += 1) {
      const x = 20 + R() * 335; const y0 = 40 + R() * 120;
      const cf = mk(front, 'gq-xconf', { left: `${x}px`, top: `${y0}px` });
      const dur = 2600 + R() * 1800; const dx = (R() * 2 - 1) * 40;
      A(cf, [{ transform: 'translate(0,0) rotate(45deg) scale(.4)', opacity: 0 }, { opacity: 1, offset: 0.15 }, { transform: `translate(${dx.toFixed(0)}px,${(380 + R() * 240).toFixed(0)}px) rotate(${(225 + R() * 360).toFixed(0)}deg) scale(1)`, opacity: 0 }], { duration: dur, delay: 1600 + R() * 900, easing: 'cubic-bezier(.3,.1,.6,1)' });
    }

    /* then the prize rises and the copy arrives one line at a time */
    A(pz, [{ transform: 'translateY(0) scale(1)' }, { transform: `translateY(${PRIZE_RISE}px) scale(.94)` }], { duration: 750, delay: 2250, easing: 'cubic-bezier(.3,0,.2,1)', fill: 'forwards' });
    await wait(2700); if (!ok()) return;
    prod.animate([{ translate: '0 0' }, { translate: '0 -7px' }, { translate: '0 0' }], { duration: 3400, iterations: Infinity, easing: 'ease-in-out' });
    halo.animate([{ opacity: 0.3 }, { opacity: 0.22 }, { opacity: 0.3 }], { duration: 2800, iterations: Infinity, easing: 'ease-in-out' });
    setRevealed(true);
  };

  /* press-and-hold on the button fills it over HOLD_MS while the chest
     rattles and glows harder; letting go drains it back. A tap on the chest
     itself opens it straight away. */
  const stopHold = (drain) => {
    cancelAnimationFrame(hold.current.raf); hold.current.raf = 0;
    setHolding(false);
    const sh = refs.shake.current; const hf = refs.hf.current;
    if (sh) { sh.style.transform = ''; sh.style.filter = ''; }
    if (hf && drain) {
      hf.style.transition = 'width .35s ease'; hf.style.width = '0';
      later(() => { if (refs.hf.current) refs.hf.current.style.transition = ''; }, 360);
    }
  };
  const holdFrame = (now) => {
    const p = Math.min(1, (now - hold.current.t0) / HOLD_MS);
    const hf = refs.hf.current; const sh = refs.shake.current; const tt = now / 1000;
    if (hf) hf.style.width = `${p * 100}%`;
    if (sh) {
      sh.style.transform = `translate(${(Math.sin(tt * 55) * p * 2.4).toFixed(2)}px,${(Math.cos(tt * 47) * p * 1.2).toFixed(2)}px) rotate(${(Math.sin(tt * 38) * p * 3).toFixed(2)}deg)`;
      sh.style.filter = `drop-shadow(0 0 ${(12 + p * 22).toFixed(0)}px rgba(253,189,24,${(0.5 + p * 0.5).toFixed(2)}))`;
    }
    if (now - hold.current.lastVib > Math.max(55, 140 - p * 90)) { vib(Math.round(6 + p * 18)); hold.current.lastVib = now; }
    if (p >= 1) { stopHold(false); openChest(); return; }
    hold.current.raf = requestAnimationFrame(holdFrame);
  };
  const startHold = (e) => {
    if (locked || stage < 3 || opened || opening.current) return;
    e.preventDefault();
    setHolding(true);
    const hf = refs.hf.current;
    if (hf) hf.style.transition = '';
    hold.current.t0 = performance.now() - ((parseFloat(hf && hf.style.width) || 0) / 100) * HOLD_MS;
    hold.current.lastVib = 0;
    hold.current.raf = requestAnimationFrame(holdFrame);
  };
  const endHold = () => { if (hold.current.raf) stopHold(true); };

  /* a tap on the chest: locked, it wobbles, the lock chip pulses and a toast
     says when it opens; openable, it opens */
  const tapChest = () => {
    if (stage < 2) return;
    if (!locked) {
      if (stage < 3 || opened || opening.current || hold.current.raf) return;
      const hf = refs.hf.current;
      if (hf) { hf.style.transition = 'width .3s ease'; hf.style.width = '100%'; }
      openChest();
      return;
    }
    if (refs.shake.current) refs.shake.current.animate([{ transform: 'rotate(0)' }, { transform: 'rotate(-5deg)' }, { transform: 'rotate(5deg)' }, { transform: 'rotate(-3deg)' }, { transform: 'rotate(0)' }], { duration: 420 });
    if (refs.lock.current) refs.lock.current.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], { duration: 360 });
    vib([10, 30, 10]);
    if (onToast) onToast(`Your chest opens on ${fmtDayLong(drawOn)}`);
  };

  /* ---- render ---- */
  const eyebrow = door ? `Question ${view.i + 1} of ${path.length}` : 'Question 1';
  const hint = `${Q.hint}${limited ? ` · ${count}/${Q.max}` : ''}`;
  const qCls = ['gq-sv-q', leaving ? 'gq-out' : (view.dir ? 'gq-in' : '')].filter(Boolean).join(' ');
  const doneCls = [
    'gq-sv-done', stage >= 1 ? 'gq-show' : '', stage >= 2 ? 'gq-txt' : '', stage >= 3 ? 'gq-ready' : '',
    locked ? 'gq-locked' : '', opened ? 'gq-opened' : '', revealed ? 'gq-revealed' : '',
  ].filter(Boolean).join(' ');
  const prize = prizeFor(chest);
  /* the copy under the chest: locked → when to come back; openable → how to
     open it; opened → what was inside */
  let dh = 'Golden Chest claimed!';
  let dp = `Come back on ${fmtDayLong(drawOn)} to open it.`;
  if (opened) { dh = prize.title; dp = prize.sub; } else if (!locked) dp = 'Hold the button or tap the chest to open it.';

  return (
    <div className="gq-overlay gq-survey-ov">
      <div className={`gq-survey${phase === 'claiming' ? ' gq-claiming' : ''}${quote ? ' gq-xmode' : ''}`} data-dir={view.dir || undefined} style={{ zoom, height: stageHeight }} role="dialog" aria-modal="true" aria-label={quote ? 'Quote & Claim reward' : 'Quick survey'}>
        <BgRot />

        {/* the questions and their chrome — not in quote mode (prototype .survey.xmode) */}
        {!quote && (
          <>
            <div className="gq-sv-hdr">
              <button type="button" className="gq-back" onClick={goBack} aria-label="Back"><BackIcon /></button>
              <div className="gq-ttl">Quick survey</div>
              <button type="button" className="gq-sv-x" onClick={exit} aria-label="Close"><CloseIcon /></button>
            </div>
            <div className="gq-sv-prog" ref={refs.prog} aria-hidden="true">
              {Array.from({ length: total }, (_, k) => (
                <i key={k} className={k < view.i || (k === view.i && answered) || phase === 'claiming' ? 'gq-on' : ''} />
              ))}
            </div>
            <div className="gq-sv-reward"><img src={chestc} alt="" /><span><BoldChest text={chipText(copy, pr)} /></span></div>

            <div className={qCls} key={view.seq} ref={refs.q}>
              <div className="gq-sv-eyebrow">{eyebrow}</div>
              <div className="gq-sv-qt">{Q.q}</div>
              <div className="gq-sv-hint" ref={refs.hint}>{hint}</div>
              {Q.grid ? (
                <div className="gq-sv-grid">
                  {Q.opts.map((o, v) => (
                    <button key={v} type="button" className={`gq-sv-chip${selected(v) ? ' gq-sel' : ''}`} aria-pressed={selected(v)} onClick={() => pick(v)}>
                      {o}<span className="gq-ck"><Check /></span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className={`gq-sv-opts ${many ? 'gq-many' : 'gq-one'}`}>
                  {Q.opts.map((o, v) => (
                    <button key={v} type="button" className={`gq-sv-opt${selected(v) ? ' gq-sel' : ''}${full && !selected(v) ? ' gq-dim' : ''}`} aria-pressed={selected(v)} onClick={() => pick(v)}>
                      <span>{o}</span><span className="gq-ck"><Check /></span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <GoldButton className="gq-sv-next" disabled={!answered} onClick={goNext}>
              {last ? <><img src={chestc} alt="" />{copy.finishCta}</> : copy.nextCta}
            </GoldButton>

            {/* the "Survey complete" beat */}
            <div className="gq-sv-complete" ref={refs.comp} aria-hidden={phase !== 'claiming'}>
              <div className="gq-ok">
                <svg viewBox="0 0 96 96">
                  <defs>
                    <linearGradient id="gqsv-okg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FFF4C8" /><stop offset=".5" stopColor="#F4C04A" /><stop offset="1" stopColor="#D08A12" /></linearGradient>
                  </defs>
                  <circle cx="48" cy="48" r="46" fill="url(#gqsv-okg)" />
                  <path className="gq-tk" d="M29 49.5l12.5 12.5L67 36" fill="none" stroke="#3B1D6B" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="gq-t1">Survey complete</div>
              <div className="gq-t2">Thanks for sharing! Your reward is on its way…</div>
            </div>
          </>
        )}

        {/* the reward stage: the Golden Chest lands on its pillar — locked till the
            reveal day, openable from then on */}
        <div className={doneCls} ref={refs.done} aria-hidden={stage === 0}>
          <div className="gq-sv-stage" ref={refs.stage}><div className="gq-sv-vig" /></div>
          {/* the opening's light, behind the chest */}
          <div className="gq-svx-back" ref={refs.back} aria-hidden="true" />
          <div className="gq-sv-fx" ref={refs.fx}><div className="gq-sv-rays" /><div className="gq-sv-glow" /></div>
          <div className="gq-sv-pillar" ref={refs.pillar}>
            <svg viewBox="0 0 340 420" preserveAspectRatio="xMidYMin meet">
              <defs>
                <linearGradient id="gqsv-plb" x1="0" x2="1"><stop offset="0" stopColor="#22103F" /><stop offset=".3" stopColor="#3D1D78" /><stop offset=".52" stopColor="#5B32A6" /><stop offset=".74" stopColor="#3A1B72" /><stop offset="1" stopColor="#1E0E38" /></linearGradient>
                <radialGradient id="gqsv-plt" cx=".5" cy=".42" r=".62"><stop offset="0" stopColor="#7F55CF" /><stop offset=".65" stopColor="#5A33A3" /><stop offset="1" stopColor="#46268A" /></radialGradient>
              </defs>
              <path d="M10 38 V420 H330 V38 Z" fill="url(#gqsv-plb)" />
              <ellipse cx="170" cy="38" rx="160" ry="36" fill="url(#gqsv-plt)" />
            </svg>
          </div>
          <div className="gq-sv-cshadow" ref={refs.cshadow}>
            <svg viewBox="0 0 375 812" width="375" height="812">
              <defs>
                <clipPath id="gqsv-pfc"><ellipse cx="187.5" cy="368" rx="158" ry="35" /></clipPath>
                <filter id="gqsv-shb" x="-30%" y="-80%" width="160%" height="260%"><feGaussianBlur stdDeviation="4" /></filter>
                <filter id="gqsv-shb2" x="-10%" y="-200%" width="120%" height="500%"><feGaussianBlur stdDeviation="1.8" /></filter>
                <linearGradient id="gqsv-shg" gradientUnits="userSpaceOnUse" x1="200" y1="350" x2="250" y2="398"><stop offset="0" stopColor="#0B0318" stopOpacity=".85" /><stop offset=".6" stopColor="#0B0318" stopOpacity=".5" /><stop offset="1" stopColor="#0B0318" stopOpacity=".22" /></linearGradient>
              </defs>
              <g clipPath="url(#gqsv-pfc)">
                <polygon points="90,346 128,381 168,398 314,374 326,342 288,329" fill="url(#gqsv-shg)" filter="url(#gqsv-shb)" />
                <polyline points="96,350 128,381 272,357 287,331" fill="none" stroke="#0B0318" strokeOpacity=".85" strokeWidth="6" strokeLinejoin="round" filter="url(#gqsv-shb2)" />
              </g>
            </svg>
          </div>
          <div className="gq-sv-fog" ref={refs.fog} />
          <div className="gq-sv-dust" ref={refs.dust} />
          <div className="gq-sv-lock" ref={refs.lock}>
            <svg viewBox="0 0 12 14" fill="none" aria-hidden="true"><rect x="1.5" y="6" width="9" height="7" rx="1.8" fill="currentColor" /><path d="M3.5 6V4.4a2.5 2.5 0 0 1 5 0V6" stroke="currentColor" strokeWidth="1.6" /></svg>
            <span>Opens on {fmtDay(drawOn)}</span>
          </div>
          <div className="gq-sv-chestw" ref={refs.chest} onClick={tapChest} role="presentation">
            <div className="gq-sv-shake" ref={refs.shake}><img src={chestc} alt="" /></div>
          </div>
          {/* the opening's burst — flash, lines, Gems, confetti — over the chest */}
          <div className="gq-svx-front" ref={refs.front} aria-hidden="true" />
          {/* the prize that comes out of the chest */}
          <div className="gq-xprize" ref={refs.prize} aria-hidden={!opened}>
            <div className="gq-halo" />
            <div className="gq-floor" />
            <div className="gq-prod">{prize.art}</div>
            {[[40, 70], [258, 92], [34, 210], [262, 226], [150, 18]].map(([x, y]) => (
              <svg key={`${x}-${y}`} className="gq-tw" viewBox="0 0 24 24" style={{ left: x, top: y }}><path d="M12 0C12.6 7 17 11.4 24 12c-7 .6-11.4 5-12 12-.6-7-5-11.4-12-12 7-.6 11.4-5 12-12z" fill="#FFF6DA" /></svg>
            ))}
          </div>
          <div className="gq-sv-eye">{eyeLabel}</div>
          <div className={`gq-sv-dh${dh.length > 27 ? ' gq-long' : ''}`}>{dh}</div>
          <div className="gq-sv-dp">{dp}</div>
          {!locked && (
            <button
              type="button"
              className={`gq-dhold${holding ? ' gq-holding' : ''}`}
              onPointerDown={startHold}
              onPointerUp={endHold}
              onPointerLeave={endHold}
              onPointerCancel={endHold}
              onContextMenu={(e) => e.preventDefault()}
              aria-label="Hold to open the chest"
            >
              <span className="gq-hf" ref={refs.hf} />
              <span className="gq-hl"><img src={chestc} alt="" />Hold to open</span>
            </button>
          )}
          <GoldButton className="gq-sv-home" onClick={() => onHome(opened ? prize.won : null)}>Back to the Quest</GoldButton>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Bottom sheet introducing the survey — NOT USED by the page right now:
   "Start survey" / "Resume survey" open the questions directly. Kept as an
   optional intro step: fresh — the pitch, "2 min · 4–5 questions · Golden
   Chest", Start survey; resumed — how far they got and Resume survey.
   Rendered in a zoomed wrapper like the other sheets.
   ------------------------------------------------------------------ */
export function SurveySheet({ survey, state, open, zoom = 1, stageHeight = 812, onStart, onClose }) {
  const { copy } = survey;
  const part = state.status === 'part';
  const pr = part ? surveyProgress(survey, state) : null;
  const min = surveyMinLength(survey);
  const max = surveyMaxLength(survey);
  const span = min === max ? qWord(max) : `${min}–${max} questions`;
  return (
    <div className="gq-svsheet-wrap" style={{ zoom }}>
      <Sheet open={open} label="Quick survey" stageHeight={stageHeight} onClose={onClose} className="gq-svsheet" backdrop>
        <img className="gq-ss-art" src={artSurvey} alt="" />
        <div className="gq-ss-eye">{part ? 'SURVEY IN PROGRESS' : 'QUICK SURVEY'}</div>
        <div className="gq-ss-h">{part ? fillCount(copy.resumeTitle, pr.rem) : copy.title}</div>
        <div className="gq-ss-p">{part ? copy.resumeText : copy.text}</div>
        {part ? (
          <div className="gq-ss-prog">
            <div className="gq-bars" aria-hidden="true">
              {Array.from({ length: pr.total }, (_, k) => <i key={k} className={k < pr.done ? 'gq-on' : ''} />)}
            </div>
            <div className="gq-lab">{pr.done} of {pr.total} answered</div>
          </div>
        ) : (
          <div className="gq-ss-meta">
            <span>
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4.8V8l2.2 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              2 min
            </span>
            <span>
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 4.5h6.5M6 8h6.5M6 11.5h6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><circle cx="3.2" cy="4.5" r="1" fill="currentColor" /><circle cx="3.2" cy="8" r="1" fill="currentColor" /><circle cx="3.2" cy="11.5" r="1" fill="currentColor" /></svg>
              {span}
            </span>
            <span><img src={chestc} alt="" />Golden Chest</span>
          </div>
        )}
        <GoldButton onClick={onStart}>{part ? copy.resumeCta : copy.cta}</GoldButton>
        <button type="button" className="gq-ss-later" onClick={onClose}>{part ? copy.resumeLater : copy.later}</button>
      </Sheet>
    </div>
  );
}

/* the survey's entry on the rewards page (top of the Gems and Chests tabs) */
export function SurveyRow({ survey, state, onClick }) {
  const part = state.status === 'part';
  const pr = part ? surveyProgress(survey, state) : null;
  return (
    <button type="button" className="gq-rwsv" onClick={onClick}>
      <img src={artSurvey} alt="" />
      <div>
        <b>{part ? 'Finish your survey' : 'Claim a free Golden Chest'}</b>
        <span>{part ? `${qWord(pr.rem)} to your Golden Chest` : 'Quick survey · under 2 minutes'}</span>
      </div>
      <span className="gq-go">›</span>
    </button>
  );
}